import type { AssetTypeCode, ComplexityLevel } from '@atlas/types'
import { CardShell } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { movedOnePlace } from '@/domain/reorder'
import type { WorkflowResolverRuleDTO } from '@/domain/workflow-admin.types'
import {
	useCreateResolverRule,
	useDeleteResolverRule,
	useReorderResolverRules,
	useResolverRules,
	useUpdateResolverRule,
	useWorkflows,
} from '@/hooks/useWorkflowAdmin'
import { apiErrorMessage, apiErrorStatus } from '@/lib/api-error'
import { sortedByLabel } from '@/lib/option-order'
import { ResolverRuleForm } from './ResolverRuleForm'
import { ResolverRuleList } from './ResolverRuleList'
import { ErrorBanner } from './states'

/**
 * Resolver-rule administration. Ordering is explicit move-up/move-down against
 * the server-returned order — no drag-and-drop, and no local reordering: the
 * reorder call returns the authoritative priorities and the list is refetched.
 */

/** Move one rule one place in the current priority order and return the full id list to submit. */
export function reorderedIds(rules: WorkflowResolverRuleDTO[], ruleId: string, direction: 'up' | 'down'): string[] {
	const ordered = [...rules].sort((a, b) => a.priority - b.priority).map((rule) => rule.ruleId)
	return movedOnePlace(ordered, ruleId, direction)
}

/**
 * A refused target comes back as a 404 (a workflow this tenant cannot see) or a
 * 422 naming the workflow (inactive). Either way the message belongs against
 * the selector; anything else stays a form-level error.
 */
function targetFieldErrorOf(error: unknown): string | undefined {
	if (!error) return undefined
	const status = apiErrorStatus(error)
	if (status !== 404 && status !== 422) return undefined
	const message = apiErrorMessage(error)
	return /workflow/i.test(message) ? message : undefined
}

/** Owns the form's server state; the fields themselves are the prop-driven `ResolverRuleForm`. */
function RuleForm({
	rule,
	nextPriority,
	onClose,
}: {
	rule?: WorkflowResolverRuleDTO
	nextPriority: number
	onClose: () => void
}) {
	const create = useCreateResolverRule()
	const update = useUpdateResolverRule()
	const workflows = useWorkflows()
	const [assetTypes, setAssetTypes] = useState<string[]>(rule?.assetTypeCodes ?? [])
	const [complexities, setComplexities] = useState<string[]>(rule?.complexityLevels ?? [])
	const [workflowId, setWorkflowId] = useState(rule?.workflowId ?? '')

	const mutation = rule ? update : create
	// A refused save leaves the persisted target on screen: the rule still points
	// where the server says it does, and the message explains why the new target
	// was not taken.
	const rejected = mutation.isError ? mutation.error : undefined
	useEffect(() => {
		if (rejected && rule) setWorkflowId(rule.workflowId)
	}, [rejected, rule])

	const submit = () => {
		const matchers = {
			assetTypeCodes: assetTypes.length > 0 ? (assetTypes as AssetTypeCode[]) : null,
			complexityLevels: complexities.length > 0 ? (complexities as ComplexityLevel[]) : null,
		}
		if (rule) update.mutate({ ruleId: rule.ruleId, body: { workflowId, ...matchers } }, { onSuccess: onClose })
		else create.mutate({ workflowId, priority: nextPriority, ...matchers }, { onSuccess: onClose })
	}

	return (
		<ResolverRuleForm
			isEdit={rule !== undefined}
			assetTypes={assetTypes}
			complexities={complexities}
			workflowId={workflowId}
			workflowOptions={sortedByLabel(
				(workflows.data ?? []).map((workflow) => ({ value: workflow.workflowId, label: workflow.name })),
			)}
			onAssetTypesChange={setAssetTypes}
			onComplexitiesChange={setComplexities}
			onWorkflowIdChange={setWorkflowId}
			onSubmit={submit}
			onCancel={onClose}
			isPending={mutation.isPending}
			targetError={targetFieldErrorOf(rejected)}
			saveError={rejected}
		/>
	)
}

export function ResolverRulesPage() {
	const [editing, setEditing] = useState<{ ruleId: string | null } | null>(null)
	const rules = useResolverRules()
	const workflows = useWorkflows()
	const reorder = useReorderResolverRules()
	const remove = useDeleteResolverRule()
	const failed = [reorder, remove].find((mutation) => mutation.isError)
	const all = rules.data ?? []
	// The list keeps inactive rules on screen for administration, but priority and
	// ordering are defined by the server over the active rule set alone.
	const active = all.filter((rule) => rule.isActive)
	// Deactivation leaves a rule's priority in place, so the active priorities can hold
	// gaps — a new rule takes one past the highest active priority, never a count.
	const nextPriority = active.reduce((highest, rule) => Math.max(highest, rule.priority + 1), 0)
	const editingRule = editing?.ruleId ? all.find((rule) => rule.ruleId === editing.ruleId) : undefined

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<Link className="text-sm text-ds-primary hover:underline" to="/admin/workflows">
						← All workflows
					</Link>
					<h1 className="text-2xl font-semibold">Resolver rules</h1>
					<p className="text-ds-text-muted">The first matching rule decides a new Work Order's workflow.</p>
				</div>
				<Button onPress={() => setEditing(editing ? null : { ruleId: null })}>{editing ? 'Cancel' : 'New rule'}</Button>
			</div>
			{editing && (
				<RuleForm
					key={editing.ruleId ?? 'new'}
					rule={editingRule}
					nextPriority={nextPriority}
					onClose={() => setEditing(null)}
				/>
			)}
			{failed && <ErrorBanner error={failed.error} title="Rule change was rejected" />}
			<CardShell>
				<ResolverRuleList
					rules={all}
					workflows={workflows.data ?? []}
					isLoading={rules.isLoading}
					error={rules.isError ? rules.error : undefined}
					onRetry={() => void rules.refetch()}
					onMove={(ruleId, direction) => reorder.mutate(reorderedIds(active, ruleId, direction))}
					onEdit={(ruleId) => setEditing({ ruleId })}
					onDelete={(ruleId) => remove.mutate(ruleId)}
					isMutating={reorder.isPending || remove.isPending}
				/>
			</CardShell>
		</div>
	)
}
